import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Menu,
    ListItemIcon,
    ListItemText,
    Divider,
    Button,
    CssBaseline,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";

import dividerImg from "../../../assets/divider.png";
import { useUser } from "../../../contexts/UserContext";
import { formatDateToDDMMYYYY } from "../../../util";

const safeStr = (v) => String(v ?? "").trim();
const normalize = (s) => safeStr(s).toLowerCase();

const fmtDate = (iso) => {
    return formatDateToDDMMYYYY(iso);
};

export default function StoreOutTable({ refreshKey, onEdit }) {
    const { token } = useUser();
    const base = process.env.REACT_APP_API_BASE || "https://api.fmb52.com/api";

    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loadingFirst, setLoadingFirst] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [dishFilter, setDishFilter] = useState(""); // dish name
    const [itemFilter, setItemFilter] = useState(""); // item name
    const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD

    // Proper client-side pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Action menu
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteRow, setDeleteRow] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const headers = useMemo(
        () => ({
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
        }),
        [token]
    );

    const getUniqueDishNames = (storeOut) => {
        const items = Array.isArray(storeOut?.items) ? storeOut.items : [];
        const names = items
            .map((x) => x?.dish?.name)
            .filter(Boolean)
            .map((n) => String(n));
        return Array.from(new Set(names));
    };

    const getUniqueItemNames = (storeOut) => {
        const items = Array.isArray(storeOut?.items) ? storeOut.items : [];
        const names = items
            .map((x) => x?.item?.name)
            .filter(Boolean)
            .map((n) => String(n));
        return Array.from(new Set(names));
    };

    const getMenuText = (storeOut) => {
        // from remarks: "... | Menu: ...."
        const r = safeStr(storeOut?.remarks);
        const idx = r.toLowerCase().indexOf("menu:");
        if (idx === -1) return "-";
        return r.slice(idx + 5).trim() || "-";
    };

    const fetchPage = useCallback(async () => {
        if (!token) return;

        setLoadingFirst(true);
        try {
            const offset = page * rowsPerPage;
            const payload = {
                offset,
                limit: rowsPerPage,
                ...(safeStr(search) ? { search: safeStr(search) } : {}),
                ...(safeStr(dishFilter) ? { dish: safeStr(dishFilter) } : {}),
                ...(safeStr(itemFilter) ? { item: safeStr(itemFilter) } : {}),
                ...(safeStr(dateFilter) ? { date: safeStr(dateFilter) } : {}),
            };

            const url = `${base}/store-out`;
            const resp = await fetch(url, { method: "POST", headers, body: JSON.stringify(payload) });
            if (!resp.ok) throw new Error("Failed to fetch store-out");

            const json = await resp.json();
            const pageRows = Array.isArray(json?.data) ? json.data : [];
            setRows(pageRows);
            setTotalRows(Number(json?.pagination?.total) || pageRows.length || 0);
        } catch (e) {
            console.error("StoreOutTable error:", e);
            setRows([]);
            setTotalRows(0);
        } finally {
            setLoadingFirst(false);
        }
    }, [token, base, headers, page, rowsPerPage, search, dishFilter, itemFilter, dateFilter]);

    // Initial/page-size/page load
    useEffect(() => {
        if (token) fetchPage();
    }, [token, refreshKey, fetchPage]);

    // Reset to first page when filters change
    useEffect(() => {
        setPage(0);
    }, [search, dishFilter, itemFilter, dateFilter]);

    // Dropdown options from full data
    const dishOptions = useMemo(() => {
        const set = new Set();
        for (const r of rows) for (const d of getUniqueDishNames(r)) set.add(String(d));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const itemOptions = useMemo(() => {
        const set = new Set();
        for (const r of rows) for (const it of getUniqueItemNames(r)) set.add(String(it));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const handleExportExcel = () => {
        const params = new URLSearchParams();
        if (search?.trim()) params.set("search", search.trim());
        if (dishFilter) params.set("dish", dishFilter);
        if (itemFilter) params.set("item", itemFilter);
        if (dateFilter) params.set("date", dateFilter);

        // Change when your export endpoint ready:
        const url = `${base}/store-out/export/excel?${params.toString()}`;
        window.open(url, "_blank");
    };

    // Menu handlers
    const openMenu = (event, row) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };
    const closeMenu = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const openPrintUrl = (path) => {
        if (!menuRow?.id) return;
        const url = `${base}/store-out/${path}/${menuRow.id}`;
        window.open(url, "_blank");
        closeMenu();
    };

    const onUpdate = (row) => {
        if (onEdit && row) onEdit(row);
        closeMenu();
    };

    const onDelete = (row) => {
        setDeleteRow(row);
        setDeleteOpen(true);
        closeMenu();
    };

    const confirmDelete = async () => {
        if (!deleteRow?.id || !token) return;
        setDeleting(true);
        try {
            const resp = await fetch(`${base}/store-out/${deleteRow.id}`, {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                alert(data?.message || "Delete failed");
                return;
            }
            fetchPage();
            setDeleteOpen(false);
            setDeleteRow(null);
        } catch (e) {
            console.error("StoreOut delete error:", e);
            alert("Something went wrong");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    pt: 2,
                    pb: 3,
                    pl: 3,
                    pr: 3,
                    mr: 2,
                    ml: 2,
                    mb: 1,
                    backgroundColor: "#fff",
                    border: "1px solid #F4EBD0",
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                {/* Header + Filters */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 1.5,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Store Out
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                        }}
                    >
                        <TextField
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            placeholder="Search..."
                            sx={{ width: { xs: "100%", sm: 200 } }}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{ display: "flex", alignItems: "center", mr: 1, color: "text.secondary" }}>
                                        <SearchIcon fontSize="small" />
                                    </Box>
                                ),
                            }}
                        />

                        {/* Dish filter */}
                        <FormControl size="small" sx={{ width: { xs: "100%", sm: 200 } }}>
                            <InputLabel id="store-out-dish-label">Dishes</InputLabel>
                            <Select
                                labelId="store-out-dish-label"
                                value={dishFilter}
                                label="Dishes"
                                onChange={(e) => setDishFilter(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {dishOptions.map((d) => (
                                    <MenuItem key={d} value={d}>
                                        {d}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Item filter */}
                        <FormControl size="small" sx={{ width: { xs: "100%", sm: 220 } }}>
                            <InputLabel id="store-out-item-label">Items</InputLabel>
                            <Select
                                labelId="store-out-item-label"
                                value={itemFilter}
                                label="Items"
                                onChange={(e) => setItemFilter(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {itemOptions.map((nm) => (
                                    <MenuItem key={nm} value={nm}>
                                        {nm}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Date */}
                        <TextField
                            type="date"
                            size="small"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            sx={{ width: { xs: "100%", sm: 170 } }}
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Export */}
                        <Button
                            variant="contained"
                            onClick={handleExportExcel}
                            startIcon={<FileDownloadOutlinedIcon />}
                            sx={{ height: 40, borderRadius: 2 }}
                        >
                            Export Excel
                        </Button>
                    </Box>
                </Box>

                {/* Divider */}
                <Box
                    sx={{
                        width: "calc(100% + 32px)",
                        ml: "-16px",
                        mr: "-16px",
                        height: { xs: 10, sm: 15 },
                        backgroundImage: `url(${dividerImg})`,
                        backgroundSize: "contain",
                        backgroundRepeat: "repeat-x",
                        backgroundPosition: "center",
                        my: 2,
                    }}
                />

                <CardContent sx={{ pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
                    {loadingFirst ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 0,
                                borderRadius: 2,
                                flex: 1,
                                maxHeight: { xs: 520, md: 560 },
                                overflowY: "auto",
                                "&::-webkit-scrollbar": { width: "8px" },
                                "&::-webkit-scrollbar-thumb": { backgroundColor: "transparent", borderRadius: "10px" },
                                "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.25)" },
                            }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Hijri Date</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Miqaat</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Remarks</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Menu</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Thali</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "right", width: 80 }}>
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                No records found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{ transition: "0.2s", "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" } }}
                                            >
                                                <TableCell sx={{ fontWeight: 700 }}>{fmtDate(r.date)}</TableCell>
                                                <TableCell sx={{ color: "text.secondary" }}>{r.hijri_date || "-"}</TableCell>
                                                <TableCell sx={{ color: "text.secondary" }}>{r.miqat || "-"}</TableCell>

                                                <TableCell sx={{ color: "text.secondary", maxWidth: 380 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }} noWrap>
                                                        {r.remarks || "-"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Updated: {fmtDate(r.updated_at)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ color: "text.secondary" }}>{r.menu || getMenuText(r) || "-"}</TableCell>

                                                <TableCell sx={{ textAlign: "right", fontWeight: 800 }}>
                                                    {r.no_of_thaali ?? 0}
                                                </TableCell>

                                                <TableCell sx={{ textAlign: "right" }}>
                                                    <IconButton size="small" onClick={(e) => openMenu(e, r)} aria-label="more">
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!loadingFirst && (
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        />
                    )}
                </CardContent>

                {/* Action Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem onClick={() => openPrintUrl("print_with_items")}>
                        <ListItemIcon>
                            <LocalPrintshopOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Print Detailed</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => openPrintUrl("print")}>
                        <ListItemIcon>
                            <LocalPrintshopOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Print Niyaz Karnar</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={() => onUpdate(menuRow)}>
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Update</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={() => onDelete(menuRow)} sx={{ color: "error.main" }}>
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>

                <Dialog
                    open={deleteOpen}
                    onClose={() => (!deleting ? setDeleteOpen(false) : null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle sx={{ fontWeight: 800 }}>Confirm Delete</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2">
                            Are you sure you want to delete this store out record?
                            {deleteRow?.miqat && (
                                <> Miqaat: <b>{deleteRow.miqat}</b></>
                            )}
                            {deleteRow?.date && (
                                <> Date: <b>{fmtDate(deleteRow.date)}</b></>
                            )}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            variant="outlined"
                            disabled={deleting}
                            onClick={() => {
                                setDeleteOpen(false);
                                setDeleteRow(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={deleting}
                            onClick={confirmDelete}
                            sx={{ minWidth: 120 }}
                        >
                            {deleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
